import { fetchAuthSession } from "@aws-amplify/auth";
import { mockAuth } from "./MockAuth";
import { BASE_URL } from "./constants";

const getAuthToken = async (): Promise<string> => {
  const useMockAuth =
    process.env.REACT_APP_USE_MOCK_AUTH === "true" &&
    process.env.NODE_ENV === "development";

  const authService = useMockAuth
    ? async () => await mockAuth.currentSession()
    : fetchAuthSession;

  const token = (await authService()).tokens?.idToken?.toString();

  if (!token) {
    throw new Error("No auth token found");
  }

  return token;
};

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export interface ApiFetchOptions {
  method?: string;
  body?: unknown;
  searchParams?: Record<string, string | number | boolean | undefined | null>;
  /**
   * Version prefix to resolve `path` against. Defaults to the /v1 admin API
   * every other caller uses; pass BASE_URL_V2 for the member meeting routes
   * the panel borrows.
   */
  baseUrl?: string;
}

export const apiFetch = async <T>(
  path: string,
  options: ApiFetchOptions = {}
): Promise<T> => {
  const token = await getAuthToken();
  const { method = "GET", body, searchParams, baseUrl = BASE_URL } = options;

  let url = `${baseUrl}${path}`;

  if (searchParams) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(searchParams)) {
      if (value !== undefined && value !== null && value !== "") {
        params.append(key, String(value));
      }
    }
    const paramStr = params.toString();
    if (paramStr) {
      url += `?${paramStr}`;
    }
  }

  const response = await fetch(url, {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: token,
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: response.statusText }));
    // Some routes answer with a bare JSON string rather than an object — the
    // shared JsonResponse.forbidden/notFound take `any` and several handlers
    // pass the reason text straight in. Reading `.message` off a string yields
    // undefined, so without this the caller shows a bare status code in exactly
    // the cases where the server did explain itself.
    const message =
      typeof error === "string"
        ? error
        : error?.message || `Request failed: ${response.status}`;
    const apiError = new ApiError(message, response.status);
    throw apiError;
  }

  return response.json();
};
